/**
 * Comprehensive AI Provider modal - manage providers, select models, and test chat
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Card } from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Combobox } from "@/components/ui/combobox.tsx";
import { useAuth } from "@/hooks/useAuth.ts";
import { useModels } from "@/hooks/useModels.ts";
import { getAllProviders, getProviderConfig } from "@/lib/ai/config.ts";
import type { AIProvider, AuthTokens } from "@/lib/auth/tokens.ts";
import type { ModelInfo } from "@/types/models.ts";
import { Brain, Check, Image, Loader2, Paperclip, Plus, Wrench } from "lucide-react";
import { CopilotAuthModal } from "@/components/auth/CopilotAuthModal.tsx";
import { AntigravityClient } from "@/lib/ai/antigravity.ts";

interface AIProviderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
}

export function AIProviderModal({
  open,
  onOpenChange,
  selectedModel,
  onModelChange,
}: AIProviderModalProps) {
  const { t } = useTranslation();
  const {
    provider: activeProvider,
    connectedProviders,
    setActiveProvider,
    logoutProvider,
    login,
    tokens,
  } = useAuth();

  const allProviders = getAllProviders();
  const { models, isLoading: modelsLoading, error: modelsError } = useModels(
    activeProvider || undefined,
  );

  const [localSelectedModel, setLocalSelectedModel] = useState(selectedModel || "");
  const [showCopilotAuth, setShowCopilotAuth] = useState(false);
  const [showProviderPicker, setShowProviderPicker] = useState(false);
  const [selectedNewProvider, setSelectedNewProvider] = useState<AIProvider | null>(null);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [apiKeyError, setApiKeyError] = useState("");

  // Test chat state
  const [testMessage, setTestMessage] = useState("");
  const [testResponse, setTestResponse] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState("");

  // Mirror externally controlled model state (empty means "auto")
  useEffect(() => {
    setLocalSelectedModel(selectedModel ?? "");
  }, [selectedModel]);

  const handleModelChange = (model: string) => {
    setLocalSelectedModel(model);
    onModelChange?.(model);

    // Store per-provider model preference in localStorage
    if (activeProvider) {
      try {
        if (model) {
          localStorage.setItem(`ai_model_${activeProvider}`, model);
        } else {
          localStorage.removeItem(`ai_model_${activeProvider}`);
        }
      } catch {
        // Ignore storage failures in private mode or restricted environments.
      }
    }
  };

  const getModelIcons = (model: ModelInfo) => {
    const icons: Array<{ key: string; label: string; Icon: typeof Brain }> = [];

    const modalities = new Set<string>();
    model.modalities?.input?.forEach((m) => modalities.add(m));
    model.modalities?.output?.forEach((m) => modalities.add(m));

    if (modalities.has("image")) icons.push({ key: "image", label: "image", Icon: Image });
    if (model.attachment) icons.push({ key: "attachment", label: "attachments", Icon: Paperclip });
    if (model.reasoning) icons.push({ key: "reasoning", label: "reasoning", Icon: Brain });
    if (model.tool_call) icons.push({ key: "tools", label: "tools", Icon: Wrench });

    return icons;
  };

  const selectedModelInfo = models.find((model) => model.id === localSelectedModel);

  const handleProviderConnect = async (provider: AIProvider) => {
    const config = getProviderConfig(provider);
    setShowProviderPicker(false);
    setSelectedNewProvider(null);
    setApiKeyError("");

    if (config?.authType === "api_key") {
      const trimmedKey = apiKeyValue.trim();
      if (!trimmedKey) {
        setApiKeyError("API key is required");
        setShowProviderPicker(true);
        return;
      }

      login({
        provider,
        accessToken: trimmedKey,
      });
      setActiveProvider(provider);
      setApiKeyValue("");
      return;
    }

    if (provider === "copilot") {
      setShowCopilotAuth(true);
    } else if (provider === "antigravity") {
      await startOAuthFlow(AntigravityClient, provider);
    }
  };

  const startOAuthFlow = (
    _ClientClass: typeof AntigravityClient,
    provider: AIProvider,
  ) => {
    try {
      const endpoint = "/api/auth/google/start";
      sessionStorage.setItem("oauth_provider", provider);
      globalThis.location.href = endpoint;
    } catch (error) {
      console.error("Failed to start OAuth flow:", error);
    }
  };

  const handleCopilotSuccess = (tokens: AuthTokens) => {
    login(tokens);
    setShowCopilotAuth(false);
  };

  const handleDisconnect = (provider: AIProvider) => {
    logoutProvider(provider);

    // If we disconnected the active provider, clear the model
    if (provider === activeProvider) {
      setLocalSelectedModel("");
      onModelChange?.("");
    }
  };

  const sendTestMessage = async () => {
    if (!testMessage.trim() || !tokens) return;

    setTestLoading(true);
    setTestError("");
    setTestResponse("");

    try {
      const requestBody: Record<string, unknown> = {
        provider: tokens.provider,
        model: localSelectedModel || undefined,
        messages: [{ role: "user", content: testMessage }],
        max_tokens: 100,
      };

      if (tokens.accessToken && !tokens.accessToken.includes("cookie")) {
        requestBody.accessToken = tokens.accessToken;
      }

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`API error: ${res.status} - ${errorText}`);
      }

      // Handle SSE streaming response
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let responseText = "";
      let buffer = "";

      if (!reader) {
        throw new Error("Response body is not readable");
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        // Keep the last (possibly incomplete) chunk in the buffer
        buffer = events.pop() ?? "";

        for (const event of events) {
          const line = event.trim();
          if (!line || !line.startsWith("data: ")) continue;

          const data = line.slice(6); // Remove "data: " prefix
          if (!data.trim()) continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.type === "chunk" && parsed.content) {
              responseText += parsed.content;
              setTestResponse(responseText); // Update UI progressively
            } else if (parsed.type === "error") {
              throw new Error(parsed.message || "Stream error");
            }
          } catch (e) {
            // Ignore malformed JSON chunks (partial reads)
            if (e instanceof Error && e.message !== "Unexpected end of JSON input") {
              throw e;
            }
          }
        }
      }

      // Process any remaining buffer content
      if (buffer.trim()) {
        const line = buffer.trim();
        if (line.startsWith("data: ")) {
          const data = line.slice(6).trim();
          if (data) {
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === "chunk" && parsed.content) {
                responseText += parsed.content;
                setTestResponse(responseText);
              } else if (parsed.type === "error") {
                throw new Error(parsed.message || "Stream error");
              }
            } catch {
              // Ignore malformed final chunk
            }
          }
        }
      }

      if (!responseText) {
        setTestResponse("No response content found");
      }
    } catch (err) {
      setTestError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Provider Settings</DialogTitle>
            <DialogDescription>
              Manage AI providers, select models, and test connectivity
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Connected Providers Section */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Connected Providers</h3>

              {connectedProviders.length === 0
                ? (
                  <p className="text-sm text-muted-foreground mb-3">
                    No providers connected yet. Add one below.
                  </p>
                )
                : (
                  <div className="space-y-2 mb-3">
                    {connectedProviders.map((provider) => {
                      const config = getProviderConfig(provider);
                      const isActive = provider === activeProvider;

                      return (
                        <Card
                          key={provider}
                          className={`p-3 ${
                            isActive ? "border-green-600 bg-green-50 dark:bg-green-900/20" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {isActive && (
                                <div className="h-2 w-2 rounded-full bg-green-600 animate-pulse" />
                              )}
                              <div>
                                <p className="font-medium text-sm">{config?.name}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {!isActive && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setActiveProvider(provider)}
                                >
                                  Activate
                                </Button>
                              )}
                              {isActive && <Check className="h-4 w-4 text-green-600" />}
                              {provider !== "default" && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDisconnect(provider)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  Disconnect
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowProviderPicker(!showProviderPicker)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Provider
              </Button>

              {/* Provider picker inline section */}
              {showProviderPicker && (
                <div className="mt-3 space-y-2 p-3 border rounded-md bg-muted/30">
                  <p className="text-xs font-medium mb-2">Select a provider to connect:</p>
                  {allProviders
                    .filter((p) => p.id !== "default" && !connectedProviders.includes(p.id))
                    .map((provider) => (
                      <Card
                        key={provider.id}
                        className={`cursor-pointer border-2 p-3 transition-colors hover:border-primary ${
                          selectedNewProvider === provider.id
                            ? "border-primary bg-primary/10"
                            : "border-border"
                        }`}
                        onClick={() => setSelectedNewProvider(provider.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-sm">{provider.name}</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t(`auth.provider.${provider.id}.description`)}
                            </p>
                          </div>
                          {selectedNewProvider === provider.id && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                      </Card>
                    ))}

                  {allProviders.filter((p) =>
                        p.id !== "default" && !connectedProviders.includes(p.id)
                      ).length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      All providers are already connected!
                    </p>
                  )}

                  {selectedNewProvider &&
                    getProviderConfig(selectedNewProvider)?.authType === "api_key" && (
                    <div className="pt-2 space-y-2">
                      <Input
                        value={apiKeyValue}
                        onChange={(e) => {
                          setApiKeyValue(e.target.value);
                          if (apiKeyError) setApiKeyError("");
                        }}
                        placeholder={`Paste ${
                          getProviderConfig(selectedNewProvider)?.name || "API"
                        } key`}
                        type="password"
                      />
                      {apiKeyError && <p className="text-xs text-destructive">{apiKeyError}</p>}
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowProviderPicker(false);
                        setSelectedNewProvider(null);
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        selectedNewProvider && handleProviderConnect(selectedNewProvider)}
                      disabled={!selectedNewProvider || (
                        getProviderConfig(selectedNewProvider)?.authType === "api_key" &&
                        !apiKeyValue.trim()
                      )}
                      className="flex-1"
                    >
                      Connect
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Model Selection Section */}
            {activeProvider && (
              <div>
                <h3 className="text-sm font-semibold mb-3">Model Selection</h3>
                <div className="flex items-center gap-2">
                  <Combobox
                    items={models}
                    value={localSelectedModel}
                    onValueChange={handleModelChange}
                    disabled={modelsLoading || models.length === 0}
                    placeholder={modelsLoading ? "Loading models..." : "Auto (provider default)"}
                    searchPlaceholder={modelsLoading ? "Loading models..." : "Search models..."}
                    emptyMessage="No matches"
                    triggerClassName="flex-1"
                    showClear
                    clearLabel="Clear model"
                    itemToValue={(model) => model.id}
                    itemToLabel={(model) => model.name || model.id}
                    itemToKeywords={(model) =>
                      [model.name, model.id, model.family, model.knowledge].filter(
                        (keyword): keyword is string => Boolean(keyword),
                      )}
                    renderItem={(model) => {
                      const icons = getModelIcons(model);
                      const isFree = Boolean(
                        model.cost && model.cost.input === 0 && model.cost.output === 0,
                      );

                      return (
                        <div className="flex min-w-0 items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">
                              {model.name || model.id}
                            </div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {model.id}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isFree && (
                              <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-foreground/70">
                                Free
                              </span>
                            )}
                            <div className="flex items-center gap-1">
                              {icons.map(({ key, label, Icon }) => (
                                <span key={`${model.id}-${key}`} title={label}>
                                  <Icon
                                    className="h-3.5 w-3.5 text-muted-foreground"
                                    aria-label={label}
                                  />
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  {modelsLoading && <Loader2 className="h-5 w-5 animate-spin" />}
                </div>

                {selectedModelInfo && (
                  <div className="mt-2 rounded-md border bg-muted/20 p-2">
                    <div className="text-xs font-medium text-foreground/90">
                      {selectedModelInfo.name || selectedModelInfo.id}
                    </div>
                    <div className="text-[11px] text-muted-foreground break-all">
                      {selectedModelInfo.id}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {selectedModelInfo.cost?.input === 0 &&
                        selectedModelInfo.cost?.output === 0 && (
                        <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-foreground/70">
                          Free
                        </span>
                      )}
                      {getModelIcons(selectedModelInfo).map(({ key, label, Icon }) => (
                        <span
                          key={`${selectedModelInfo.id}-${key}`}
                          title={label}
                          className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground"
                        >
                          <Icon className="mr-1 h-3 w-3" aria-label={label} />
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {modelsError && (
                  <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md text-xs text-yellow-800 dark:text-yellow-200">
                    Models error: {modelsError}
                  </div>
                )}
              </div>
            )}

            {/* Test Chat Section */}
            {activeProvider && (
              <div>
                <h3 className="text-sm font-semibold mb-3">Test Connection</h3>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      placeholder="Type a test message..."
                      onKeyDown={(e) => e.key === "Enter" && !testLoading && sendTestMessage()}
                      disabled={testLoading}
                      className="flex-1"
                    />
                    <Button
                      onClick={sendTestMessage}
                      disabled={testLoading || !testMessage.trim()}
                    >
                      {testLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                    </Button>
                  </div>

                  {testError && (
                    <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                      Error: {testError}
                    </div>
                  )}

                  {testResponse && (
                    <div className="p-3 bg-muted rounded-md">
                      <p className="text-xs font-medium mb-1 text-muted-foreground">Response:</p>
                      <p className="text-sm">{testResponse}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Copilot Auth Modal */}
      <CopilotAuthModal
        open={showCopilotAuth}
        onOpenChange={setShowCopilotAuth}
        onSuccess={handleCopilotSuccess}
      />
    </>
  );
}
